import React from "react";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";
import {
  isInTrialMode,
  Lesson,
  LessonAward,
  visibleLessonAwards,
} from "../../common/api";
import { Account } from "../../common/context/AccountContext";
import {
  AwardModal,
  AwardSticker,
  STICKER_PLACEHOLDER_URL,
} from "../AwardSticker";
import {
  DragScrollRail,
  FadedRailFrame,
  SectionLabel,
} from "../../common/CommonStyles";
import { useDragToScroll, useHasHorizontalOverflow } from "../../common/utils";
import { faGem } from "@fortawesome/pro-duotone-svg-icons";
import {
  DuotoneIcon,
  STAT_ICONS,
  statIconColors,
} from "../profile/ProfileIcons";
import {
  COLOR_GEM_STICKER,
  COLOR_GEM_STICKER_BACKGROUND,
  COLOR_GEM_STICKER_GEM,
  COLOR_GEM_STICKER_GEM_FACET,
  COLOR_SHADOW_LIGHT,
  COLOR_TEXT_TERTIARY,
} from "../../common/colors";
import {
  GENERIC_ITEM_BORDER_RADIUS,
  RAIL_EDGE_FADE_WIDTH,
} from "../../common/constants";
import { SPACE_MD } from "../../common/spacing";

const AWARD_SIZE = 76;
const GEM_SIZE = AWARD_SIZE * 0.42;
const GEM_QUANTITY_FONT_SIZE = AWARD_SIZE * 0.18;
const GEM_CLUSTER_WIDTH = GEM_SIZE * 1.5;
const GEM_CLUSTER_SIDE_WIDTH = GEM_SIZE * 0.5613;
const GEM_CLUSTER_SIDE_OFFSET_X = GEM_SIZE * 0.4688;
const GEM_CLUSTER_SIDE_OFFSET_Y = GEM_SIZE * 0.1875;
const GEM_CLUSTER_SIDE_ROTATION_DEGREES = 15;
const EARNED_GEM_COLORS = {
  secondary: COLOR_GEM_STICKER_GEM,
  primary: COLOR_GEM_STICKER_GEM_FACET,
};
const UNEARNED_GEM_COLORS = statIconColors(STAT_ICONS.gems, true);

const RailFrame = styled(FadedRailFrame)<{ $isScrollable: boolean }>`
  ${(p) =>
    !p.$isScrollable &&
    css`
      &::after {
        display: none;
      }
    `}
`;

const Rail = styled(DragScrollRail)<{
  $isCentered: boolean;
  $isScrollable: boolean;
}>`
  display: flex;
  gap: ${SPACE_MD}px;
  ${(p) =>
    p.$isScrollable
      ? css`
          padding-right: ${RAIL_EDGE_FADE_WIDTH}px;
        `
      : css`
          overflow-x: visible;
          cursor: auto;
        `}
  ${(p) =>
    p.$isCentered &&
    css`
      & > :first-child {
        margin-left: auto;
      }

      & > :last-child {
        margin-right: auto;
      }
    `}

  & > * {
    flex: 0 0 ${AWARD_SIZE}px;
    width: ${AWARD_SIZE}px;
    height: ${AWARD_SIZE}px;
  }
`;

const StickerPlaceholder = styled.img`
  display: block;
`;

const GemStickerBox = styled.div<{ $isEarned: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: ${GENERIC_ITEM_BORDER_RADIUS}px;
  ${(p) =>
    p.$isEarned
      ? css`
          border: 1.5px solid ${COLOR_SHADOW_LIGHT};
          background-color: ${COLOR_GEM_STICKER_BACKGROUND};
          color: ${COLOR_GEM_STICKER};
        `
      : css`
          background: url("${STICKER_PLACEHOLDER_URL}") center / 100% 100%
            no-repeat;
          color: ${COLOR_TEXT_TERTIARY};
        `}
`;

const GemCluster = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  width: ${GEM_CLUSTER_WIDTH}px;
`;

const Gem = styled(DuotoneIcon)`
  && {
    position: relative;
    display: block;
    width: ${GEM_SIZE}px;
    height: ${GEM_SIZE}px;
  }
`;

const SideGem = styled(Gem)<{ $direction: -1 | 1 }>`
  left: 50%;
  top: 50%;
  && {
    position: absolute;
    width: ${GEM_CLUSTER_SIDE_WIDTH}px;
    height: ${GEM_CLUSTER_SIDE_WIDTH}px;
  }
  transform: translate(-50%, -50%)
    translate(
      ${(p) => GEM_CLUSTER_SIDE_OFFSET_X * p.$direction}px,
      ${GEM_CLUSTER_SIDE_OFFSET_Y}px
    )
    rotate(${(p) => GEM_CLUSTER_SIDE_ROTATION_DEGREES * p.$direction}deg);
`;

const GemQuantity = styled.span`
  font-size: ${GEM_QUANTITY_FONT_SIZE}px;
  font-weight: 500;
  line-height: 1;
`;

const GemSticker = ({
  gems,
  isEarned,
}: {
  gems: number;
  isEarned: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <GemStickerBox $isEarned={isEarned}>
      {isEarned ? (
        <GemCluster aria-hidden>
          <SideGem icon={faGem} {...EARNED_GEM_COLORS} $direction={-1} />
          <SideGem icon={faGem} {...EARNED_GEM_COLORS} $direction={1} />
          <Gem icon={faGem} {...EARNED_GEM_COLORS} />
        </GemCluster>
      ) : (
        <Gem icon={faGem} {...UNEARNED_GEM_COLORS} aria-hidden />
      )}
      <GemQuantity>
        {t("lesson_details_gem_quantity", { count: gems })}
      </GemQuantity>
    </GemStickerBox>
  );
};

export const railStickerAwards = (lesson: Lesson, account: Account) =>
  isInTrialMode(account.user) ||
  lesson.is_studio_project ||
  lesson.web_content_url
    ? []
    : visibleLessonAwards(
        lesson.awards,
        account.profileState?.unlocked_award_uuids || []
      );

export const AwardStickerRail = ({
  lesson,
  account,
  leading,
  trailing,
  isCentered = false,
  className,
}: {
  lesson: Lesson;
  account: Account;
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
  isCentered?: boolean;
  className?: string;
}) => {
  const [modalAward, setModalAward] = React.useState<LessonAward | null>(null);
  const dragHandlers = useDragToScroll();
  const [rail, setRail] = React.useState<HTMLDivElement | null>(null);
  const isScrollable = useHasHorizontalOverflow(rail);
  const unlockedAwardUuids = account.profileState?.unlocked_award_uuids || [];
  const stickers = railStickerAwards(lesson, account);

  if (!leading && !trailing && stickers.length === 0) {
    return null;
  }

  return (
    <>
      <RailFrame className={className} $isScrollable={isScrollable}>
        <Rail
          ref={setRail}
          $isCentered={isCentered}
          $isScrollable={isScrollable}
          {...(isScrollable ? dragHandlers : {})}
        >
          {leading}
          {stickers.map((award) =>
            unlockedAwardUuids.includes(award.uuid) ? (
              <AwardSticker
                key={award.uuid}
                award={award}
                onClick={() => setModalAward(award)}
              />
            ) : (
              <StickerPlaceholder
                key={award.uuid}
                src={STICKER_PLACEHOLDER_URL}
                alt=""
              />
            )
          )}
          {trailing}
        </Rail>
      </RailFrame>
      <AwardModal award={modalAward} onClose={() => setModalAward(null)} />
    </>
  );
};

const LabeledRail = styled(AwardStickerRail)`
  margin-top: ${SPACE_MD}px;
`;

export const LessonAwards = ({
  lesson,
  account,
  isCompleted,
  className,
}: {
  lesson: Lesson;
  account: Account;
  isCompleted: boolean;
  className?: string;
}) => {
  const { t } = useTranslation();
  const gems =
    lesson.gems_earned > 1 &&
    !lesson.is_studio_project &&
    lesson.custom_flashpack_id === null
      ? lesson.gems_earned
      : null;
  if (
    isInTrialMode(account.user) ||
    (gems === null && railStickerAwards(lesson, account).length === 0)
  ) {
    return null;
  }

  return (
    <div className={className}>
      <SectionLabel>{t("lesson_details_awards")}</SectionLabel>
      <LabeledRail
        lesson={lesson}
        account={account}
        leading={
          gems !== null && <GemSticker gems={gems} isEarned={isCompleted} />
        }
      />
    </div>
  );
};
